/**
 * A single finding, framed as what it is: a risk, an opening, or a strength.
 *
 * `rootCause` is set apart rather than run into the finding — the observation
 * and the explanation for it are different claims, and the second is the one a
 * reader will argue with.
 *
 * The agent sends the metric and benchmark as sentences
 * (`"88.1% Active Usage (▲ +0.9 pts, z = 2.8)"`, `"Target: 75.6% | Etsy: 48.4%"`).
 * Where they follow that shape they are read into a headline number, a change,
 * and a bar per comparison; anything else is shown as written.
 */

import React from "react";
import PropTypes from "prop-types";
import { FiAlertTriangle, FiCompass, FiShield } from "react-icons/fi";
import { splitDeltas } from "../../utils/highlightDeltas";
import { useReply } from "./ReplyContext";
import InlineText, { stripInline } from "./InlineText";

const KINDS = {
  warning: { Icon: FiAlertTriangle, label: "Watch" },
  opportunity: { Icon: FiCompass, label: "Opportunity" },
  strength: { Icon: FiShield, label: "Strength" },
};

const NUM = String.raw`[+\-−]?\d+(?:\.\d+)?`;
// parseFloat, not Number: the value keeps its "%" ("88.1%").
const toNumber = (s) => parseFloat(String(s).replace("−", "-"));

/**
 * `"84.5% Usage vs 50.4% Recommend"` → two headline numbers, each with its
 * label, and the agent's own connecting word ("vs") kept as written.
 */
const readPair = (text) => {
  const m = new RegExp(String.raw`^\s*(${NUM}\s*%?)\s+(.+?)\s+(vs\.?|versus)\s+(${NUM}\s*%?)\s*(.*)$`, "is").exec(text);
  if (!m) return null;
  return {
    pair: [
      { value: m[1].trim(), label: m[2].trim() },
      { value: m[4].trim(), label: m[5].trim() },
    ],
    joiner: m[3],
  };
};

/**
 * Splits the metric line so its leading number can be shown large. Nothing
 * is dropped or reworded: `value` and `rest` together are the agent's text.
 *
 * - `"88.1% Active Usage (▲ +0.9 pts, z = 2.8)"` → value `88.1%`, rest
 *   `Active Usage (▲ +0.9 pts, z = 2.8)`
 * - `"Trust: 52.1 (YoY)"` → lead `Trust:`, value `52.1`, rest `(YoY)`
 * - `"84.5% Usage vs 50.4% Recommend"` → a `pair`
 *
 * Null when it is none of these; the line is then shown as written.
 */
export const readMetric = (text) => {
  if (typeof text !== "string") return null;
  const pair = readPair(text);
  if (pair) return pair;
  const lead = new RegExp(String.raw`^\s*(${NUM}\s*%?)\s+(.*)$`, "s").exec(text);
  if (lead) return { value: lead[1].trim(), rest: lead[2].trim() };
  const named = new RegExp(String.raw`^\s*([^:]{1,40}:)\s*(${NUM}\s*%?)\s*(.*)$`, "s").exec(text);
  if (named) return { lead: named[1].trim(), value: named[2].trim(), rest: named[3].trim() };
  return null;
};

/**
 * `"Target: 75.6% | Etsy: 48.4%"` → `[{ name: "Target", value: 75.6, text: "75.6%" }, …]`.
 * `text` is the figure as the agent wrote it, for display. A signed number
 * ("vs Peer: +4.0") is a gap, not a level, and cannot be a bar.
 */
export const readComparisons = (text) => {
  if (typeof text !== "string") return null;
  const items = text.split(/\s*[|;]\s*|,\s+(?=[^\d])/).map((segment) => {
    const m = new RegExp(String.raw`^\s*(.+?)\s*:\s*((\d+(?:\.\d+)?)\s*%?)\s*$`).exec(segment);
    return m ? { name: m[1], value: Number(m[3]), text: m[2] } : null;
  });
  return items.length && items.every(Boolean) ? items : null;
};

/** Text with the agent's `▲ +0.9 pts` styled as a pill, word for word. */
const WithDeltas = ({ text }) => {
  const parts = splitDeltas(text);
  if (!parts) return <InlineText text={text} />;
  return parts.map((part, i) =>
    part.type === "text" ? (
      <InlineText key={i} text={part.value} />
    ) : (
      <span key={i} className={`lou-diag__delta lou-diag__delta--${part.direction}`}>
        {part.text}
      </span>
    ),
  );
};

const clamp = (n) => Math.min(100, Math.max(0, n));

const Bars = ({ rows }) => (
  <div className="lou-diag__bars">
    {rows.map((row, i) => (
      <React.Fragment key={`${row.name}-${i}`}>
        <span className={`lou-diag__barName${i === 0 ? " lou-diag__barName--focus" : ""}`}>
          {row.name}
        </span>
        <span className="lou-diag__barTrack" aria-hidden="true">
          <span
            className={`lou-diag__barFill lou-diag__barFill--${i === 0 ? "focus" : `peer${Math.min(i, 3)}`}`}
            style={{ width: `${clamp(row.value)}%` }}
          />
        </span>
        <span className={`lou-diag__barValue${i === 0 ? " lou-diag__barValue--focus" : ""}`}>
          {row.text}
        </span>
      </React.Fragment>
    ))}
  </div>
);

const DiagnosticCard = ({
  type = "warning",
  title,
  subtitle,
  metric,
  benchmark,
  finding,
  rootCause,
}) => {
  const { brand } = useReply();
  const kind = KINDS[type] ? type : "warning";
  const { Icon, label } = KINDS[kind];

  // Marks are read past, not parsed: "**88.1%** Usage" is still 88.1%.
  const read = readMetric(stripInline(metric));
  const comparisons = readComparisons(stripInline(benchmark));
  const focus = read?.value && /%$/.test(read.value) ? toNumber(read.value) : undefined;
  // The first bar is the metric itself, named by the reply's brand (from its
  // KPI grid or funnel) or, failing that, by the metric's own label.
  const focusName = brand || (read?.rest || "").split("(")[0].trim();
  const bars =
    focus !== undefined && comparisons && focusName
      ? [{ name: focusName, value: focus, text: read.value }, ...comparisons]
      : null;

  // A comparison of two shares ("84.5% Usage vs 50.4% Recommend") draws each
  // as a bar. Nothing is derived from them: the card shows what the agent
  // sent, no more — a gap the agent did not state would be the UI's claim.
  const pairRows =
    read?.pair && read.pair.every((side) => /%$/.test(side.value))
      ? read.pair.map((side) => ({ name: side.label, value: toNumber(side.value), text: side.value }))
      : null;

  return (
    <section className={`lou-diag lou-diag--${kind}`}>
      <header className="lou-diag__head">
        <span className="lou-diag__icon" aria-hidden="true">
          <Icon size={15} />
        </span>
        <div className="lou-diag__heading">
          <span className="lou-diag__kind">{label}</span>
          <h4 className="lou-diag__title"><InlineText text={title} /></h4>
          {subtitle ? <span className="lou-diag__subtitle"><InlineText text={subtitle} /></span> : null}
        </div>
      </header>

      {/* The numbers, with a benchmark that could not become bars as their
          caption — on a line of its own it read as a leftover. */}
      {metric || (!bars && benchmark) ? (
        <div className="lou-diag__statGroup">
          {read?.pair ? (
            <div className="lou-diag__stats">
              {read.pair.map((side, i) => (
                <React.Fragment key={i}>
                  {i ? <span className="lou-diag__vs">{read.joiner}</span> : null}
                  <span className="lou-diag__value">{side.value}</span>
                  {side.label ? <span className="lou-diag__label">{side.label}</span> : null}
                </React.Fragment>
              ))}
            </div>
          ) : read ? (
            <div className="lou-diag__stats">
              {read.lead ? <span className="lou-diag__label">{read.lead}</span> : null}
              <span className="lou-diag__value">{read.value}</span>
              {read.rest ? (
                <span className="lou-diag__label">
                  <WithDeltas text={read.rest} />
                </span>
              ) : null}
            </div>
          ) : metric ? (
            <p className="lou-diag__metricText"><InlineText text={metric} /></p>
          ) : null}
          {!bars && benchmark ? (
            <span className="lou-diag__benchmark">
              <InlineText text={benchmark} />
            </span>
          ) : null}
        </div>
      ) : null}

      {bars ? <Bars rows={bars} /> : null}

      {pairRows ? <Bars rows={pairRows} /> : null}

      {finding ? <p className="lou-diag__finding"><InlineText text={finding} /></p> : null}

      {rootCause ? (
        <div className="lou-diag__cause">
          <span className="lou-diag__causeLabel">Likely cause</span>
          <p className="lou-diag__causeText"><InlineText text={rootCause} /></p>
        </div>
      ) : null}
    </section>
  );
};

Bars.propTypes = { rows: PropTypes.array };
WithDeltas.propTypes = { text: PropTypes.string };
DiagnosticCard.propTypes = {
  type: PropTypes.oneOf(["warning", "opportunity", "strength"]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  metric: PropTypes.string,
  benchmark: PropTypes.string,
  finding: PropTypes.string,
  rootCause: PropTypes.string,
};

export default DiagnosticCard;
